import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useHiredCandidates, HiredCandidateDto } from "@/modules/recruitment/hooks/useHiredCandidates"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Progress } from "@/modules/shared/ui/components/progress"
import {
    ClipboardList,
    Calendar,
    MapPin,
    Briefcase,
    ChevronRight,
    CheckCircle,
    Award,
    UserPlus,
    Filter
} from "lucide-react"

interface Application {
    id: string
    candidateName: string
    candidateEmail: string
    candidatePhone?: string
    jobId: string
    jobTitle: string
    department: string
    score: number
    hiredDate: string
    onboardingStarted: boolean
}


export default function HiredCandidatesPage() {
    const navigate = useNavigate()
    const { data: hiredCandidatesData = [], isLoading, error } = useHiredCandidates()
    const [jobFilter, setJobFilter] = useState<string>("all")
    const [hasOnboardingService] = useState(false) // This would come from company data

    // Map backend data to frontend format
    const applications: Application[] = hiredCandidatesData.map((candidate: HiredCandidateDto) => ({
        id: candidate.applicationId,
        candidateName: candidate.candidateName,
        candidateEmail: candidate.email,
        candidatePhone: candidate.phone,
        jobId: candidate.jobId,
        jobTitle: candidate.jobTitle,
        department: candidate.department,
        score: candidate.matchScore,
        hiredDate: new Date(candidate.hiredDate).toLocaleDateString(),
        onboardingStarted: candidate.onboardingStarted,
    }))

    const hiredApplications = applications

    const filteredApplications = jobFilter === "all"
        ? hiredApplications
        : hiredApplications.filter(app => app.jobId === jobFilter)

    const uniqueJobs = Array.from(new Map(hiredApplications.map(app => [app.jobId, { id: app.jobId, title: app.jobTitle }])).values())

    const handleInviteToOnboarding = (applicationId: string) => {
        if (hasOnboardingService) {

            navigate(`/onboarding?candidate=${applicationId}`)
        } else {
            alert("Onboarding service is not available for your company. Contact support to enable this feature.")
        }
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h3 className="text-lg font-medium text-foreground mb-2">Error Loading Hired Candidates</h3>
                    <p className="text-muted-foreground">Please try again later.</p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-foreground">Hired Candidates</h1>
                    <p className="text-muted-foreground mt-1">View and onboard successfully hired candidates</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm">
                        <ClipboardList className="w-4 h-4 mr-2" />
                        Export Data
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-card border border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-50 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Hired</p>
                                <p className="font-bold text-xl">{hiredApplications.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card border border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-lg">
                                <Briefcase className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">This Month</p>
                                <p className="font-bold text-xl">
                                    {hiredApplications.filter(app => {
                                        const hiredDate = new Date(app.hiredDate || '')
                                        const thisMonth = new Date()
                                        return hiredDate.getMonth() === thisMonth.getMonth() &&
                                            hiredDate.getFullYear() === thisMonth.getFullYear()
                                    }).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card border border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-50 rounded-lg">
                                <Award className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Avg Score</p>
                                <p className="font-bold text-xl">
                                    {hiredApplications.length > 0
                                        ? Math.round(hiredApplications.reduce((sum, app) => sum + app.score, 0) / hiredApplications.length) + "%"
                                        : "0%"
                                    }
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card border border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-50 rounded-lg">
                                <UserPlus className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Ready for Onboarding</p>
                                <p className="font-bold text-xl">
                                    {hiredApplications.filter(app => !app.onboardingStarted).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Job Filter */}
            <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select
                    value={jobFilter}
                    onChange={(e) => setJobFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                    <option value="all">All Jobs</option>
                    {uniqueJobs.map(job => (
                        <option key={job.id} value={job.id}>
                            {job.title} ({hiredApplications.filter(app => app.jobId === job.id).length})
                        </option>
                    ))}
                </select>
            </div>

            {/* Application Cards */}
            <div className="grid grid-cols-1 gap-4">
                {filteredApplications.map((application) => (
                    <Card key={application.id} className="bg-card border border-border hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                {/* Left Section - Candidate & Job Info */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                            <span className="text-sm font-medium text-muted-foreground">
                                                {application.candidateName.split(' ').map(n => n[0]).join('')}
                                            </span>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-lg text-foreground">{application.candidateName}</h3>
                                            <p className="text-sm text-muted-foreground">{application.candidateEmail}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                        <div className="flex items-center gap-1">
                                            <Briefcase className="w-4 h-4" />
                                            {application.jobTitle}
                                        </div>
                                        {application.candidatePhone && (
                                            <div className="flex items-center gap-1">
                                                <MapPin className="w-4 h-4" />
                                                {application.candidatePhone}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            Hired {application.hiredDate}
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
                                        <p className="text-sm text-emerald-700 font-medium">Status:</p>
                                        <p className="text-sm text-emerald-600">
                                            {application.onboardingStarted ? "Onboarding in progress" : "Ready for onboarding"}
                                        </p>
                                    </div>
                                </div>

                                {/* Right Section - Actions */}
                                <div className="flex items-center gap-6">
                                    {/* Match Score */}
                                    <div className="text-center">
                                        <Badge className="bg-emerald-100 text-emerald-700">
                                            <div className="flex items-center gap-1">
                                                <CheckCircle className="w-4 h-4" />
                                                Hired
                                            </div>
                                        </Badge>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {application.score}% match
                                        </p>
                                    </div>

                                    {/* Progress */}
                                    <div className="w-32">
                                        <Progress value={100} className="h-2" />
                                        <p className="text-xs text-muted-foreground mt-1 text-center">
                                            Completed
                                        </p>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate(`/recruitment/applications/${application.id}`)}
                                        >
                                            View Details
                                            <ChevronRight className="w-4 h-4 ml-2" />
                                        </Button>

                                        <Button
                                            variant={hasOnboardingService ? "default" : "secondary"}
                                            size="sm"
                                            onClick={() => handleInviteToOnboarding(application.id)}
                                            disabled={!hasOnboardingService}
                                            className={!hasOnboardingService ? "opacity-50 cursor-not-allowed" : ""}
                                        >
                                            <UserPlus className="w-4 h-4 mr-2" />
                                            {hasOnboardingService ? "Invite to Onboarding" : "Onboarding Unavailable"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {filteredApplications.length === 0 && (
                <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No hired candidates found</h3>
                    <p className="text-muted-foreground">Try adjusting your job filter or check back later.</p>
                </div>
            )}
        </div>
    )
}
